import { useMemo } from 'react'
import { gql } from '@apollo/client'
import { useFeatureFlags } from '@npco/mp-utils-mp-feature-flags'
import { useTranslations } from '@npco/utils-translations'
import { COLOR } from '@npco/zeller-design-system'

import { savingsAccountBannerTranslations } from '../SavingsAccountBanner.i18n'
import * as styled from '../SavingsAccountBanner.styled'
import { TermsAndConditionsEntitySavingsAccountProductFragment } from './TermsAndConditions.generated'
import { TermsAndConditionsAnchor } from './TermsAndConditionsAnchor/TermsAndConditionsAnchor'

type TermsAndConditionsProps = {
  savingsAccountProduct: TermsAndConditionsEntitySavingsAccountProductFragment
}

export const TermsAndConditions = ({
  savingsAccountProduct,
}: TermsAndConditionsProps) => {
  const t = useTranslations(savingsAccountBannerTranslations)
  const flags = useFeatureFlags()

  const textColour = flags.SavingsAccountMay ? COLOR.GREY_150 : COLOR.GREY_580

  const termsAndConditionsText = useMemo(() => {
    if (flags.SavingsAccountMay) {
      return t('termsAndConditionsMay', {
        termsAndConditionsAnchor: (
          <TermsAndConditionsAnchor colour={textColour} />
        ),
      })
    }

    return Number(savingsAccountProduct.bonusInterestRate ?? '0') === 0
      ? t('termsAndConditions', {
          termsAndConditionsAnchor: (
            <TermsAndConditionsAnchor colour={textColour} />
          ),
        })
      : t('termsAndConditionsBonus', {
          bonus: savingsAccountProduct.bonusInterestRate,
          bonusLength: savingsAccountProduct.bonusLengthInDays,
          base: savingsAccountProduct.baseInterestRate,
          termsAndConditionsAnchor: (
            <TermsAndConditionsAnchor colour={textColour} />
          ),
        })
  }, [savingsAccountProduct, textColour, t, flags.SavingsAccountMay])

  return (
    <styled.TermsAndConditionsText $colour={textColour}>
      {termsAndConditionsText}
    </styled.TermsAndConditionsText>
  )
}

TermsAndConditions.fragments = {
  EntitySavingsAccountProduct: gql`
    fragment TermsAndConditionsEntitySavingsAccountProductFragment on EntitySavingsAccountProduct {
      bonusInterestRate
      bonusLengthInDays
      baseInterestRate
      effectiveInterestRate
    }
  `,
}
